import { MysaReviewsHost } from "@theme/mysa-reviews-host";
import {
  DEFAULT_SORT,
  JUDGE_ME_OFFSET_PAGES,
  absoluteProductUrl,
  fetchWidgetPage,
  planWidgetPage,
} from "@theme/mysa-reviews-api";
import { formatLocalizedCounts, pageItems } from "@theme/mysa-reviews-ui";

/**
 * @typedef {{
 *   histogram?: HTMLElement,
 *   seeAll?: HTMLButtonElement,
 *   status?: HTMLElement,
 *   primaryList?: HTMLUListElement,
 *   primaryPagination?: HTMLElement,
 *   otherSection?: HTMLElement,
 *   otherList?: HTMLUListElement,
 *   otherPagination?: HTMLElement,
 *   sort?: HTMLSelectElement,
 *   ratingFilter?: HTMLSelectElement,
 *   cardTemplate?: HTMLTemplateElement,
 *   photoTemplate?: HTMLTemplateElement,
 * }} ReviewsWidgetRefs
 */

/**
 * First-party PDP reviews widget. Judge.me stays the data backend.
 * Pagination always replaces the current stream.
 * @extends {MysaReviewsHost}
 */
class MysaReviewsWidget extends MysaReviewsHost {
  #filterRating = 0;

  #primaryPage = 1;

  #otherPage = 1;

  #primaryTotalPages = 1;

  #otherTotalPages = 1;

  #sortBy = DEFAULT_SORT;

  /**
   * First paint / numbered pagination match the metafield SSR order (no
   * `sort_by`), which is Highest Rating. After the buyer picks a sort option,
   * always send the mapped Judge.me params — including `created_at` for Most
   * Recent. Reset on remorph so the restored SSR list stays in metafield order.
   */
  #sendSortParams = false;

  /**
   * Primary-language page counts per histogram star, filled on the first
   * deep page so Highest Rating can walk 5★ → 1★ past the 1000-review offset.
   * @type {import('@theme/mysa-reviews-api').JudgeMeRatingShard[] | null}
   */
  #ratingShards = null;

  connectedCallback() {
    super.connectedCallback();
    this.#resetFromDom();
  }

  /**
   * Combined-listing remorph restores SSR cards. Abort + bump the load token
   * so an in-flight fetch for the previous product cannot paintList over them.
   * Do not refetch here — metafield HTML is the source of truth until the
   * buyer filters, sorts, or paginates again.
   */
  updatedCallback() {
    super.updatedCallback();
    this.beginLoad();
    this.#resetFromDom();
  }

  disconnectedCallback() {
    this.killListReveal([this.refs.primaryList, this.refs.otherList]);
    super.disconnectedCallback();
  }

  #resetFromDom() {
    this.#sendSortParams = false;
    const ratingFilter = this.refs.ratingFilter;
    this.#filterRating =
      ratingFilter instanceof HTMLSelectElement
        ? Number(ratingFilter.value) || 0
        : 0;
    this.#primaryPage = Number(this.dataset.primaryPage) || 1;
    this.#otherPage = Number(this.dataset.otherPage) || 1;
    this.#primaryTotalPages = Number(this.dataset.primaryTotalPages) || 1;
    this.#otherTotalPages = Number(this.dataset.otherTotalPages) || 1;
    this.#ratingShards = null;
    const select = this.refs.sort;
    this.#sortBy =
      select instanceof HTMLSelectElement
        ? select.value || DEFAULT_SORT
        : DEFAULT_SORT;
    this.#syncFilterUi();
    formatLocalizedCounts(this, this.dataset.locale || "en");
    this.setBusy(false);
    this.setHostListPending(this.refs.primaryList, false);
    this.setHostListPending(this.refs.otherList, false);
    this.setHostStatus("");
  }

  /**
   * @param {Event} event
   */
  filterByHistogram(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const rating = Number(button.dataset.rating);
    if (!Number.isFinite(rating) || rating < 1) return;

    if (this.#filterRating === rating) {
      this.clearFilter();
      return;
    }

    this.#applyFilter(rating);
  }

  /**
   * @param {Event} event
   */
  goToPage(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const page = Number(button.dataset.page);
    const stream = button.dataset.stream === "other" ? "other" : "primary";
    if (!Number.isFinite(page) || page < 1) return;
    const total =
      stream === "other" ? this.#otherTotalPages : this.#primaryTotalPages;
    this.#replacePage(stream, Math.min(page, total || page));
  }

  /**
   * @param {Event} event
   */
  onSortChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    this.#sendSortParams = true;
    this.#sortBy = select.value || DEFAULT_SORT;
    this.#reloadLists();
  }

  /**
   * @param {Event} event
   */
  onRatingFilterChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const rating = Number(select.value);
    if (!Number.isFinite(rating) || rating < 1) {
      this.clearFilter();
      return;
    }
    if (this.#filterRating === rating) return;
    this.#applyFilter(rating);
  }

  async clearFilter() {
    this.#filterRating = 0;
    await this.#reloadLists();
  }

  /**
   * @param {number} rating
   */
  async #applyFilter(rating) {
    this.#filterRating = rating;
    this.#syncFilterUi();
    await this.#reloadLists();
  }

  async #reloadLists() {
    if (!this.reviewsCdnEnabled()) return;
    const { token, signal } = this.beginLoad();
    this.killListReveal([this.refs.primaryList, this.refs.otherList]);
    this.#hidePagination();
    this.setBusy(true);
    this.setHostListPending(this.refs.primaryList, true);
    this.setHostListPending(this.refs.otherList, true);
    this.#primaryPage = 0;
    this.#otherPage = 0;
    try {
      const loads = [this.#replaceStream("primary", 1, token, signal)];
      if (this.refs.otherList) {
        loads.push(this.#replaceStream("other", 1, token, signal));
      }
      await Promise.all(loads);
    } finally {
      if (this.isCurrentLoad(token)) {
        this.setBusy(false);
        this.setHostListPending(this.refs.primaryList, false);
        this.setHostListPending(this.refs.otherList, false);
        this.#syncFilterUi();
      }
    }
  }

  /**
   * @param {'primary' | 'other'} stream
   * @param {number} page
   */
  async #replacePage(stream, page) {
    if (!this.reviewsCdnEnabled()) return;
    const { token, signal } = this.beginLoad();
    const list =
      stream === "other" ? this.refs.otherList : this.refs.primaryList;
    this.killListReveal([list]);
    this.setBusy(true);
    this.setHostListPending(list, true);
    try {
      await this.#replaceStream(stream, page, token, signal);
    } finally {
      if (this.isCurrentLoad(token)) {
        this.setBusy(false);
        this.setHostListPending(list, false);
        this.#syncFilterUi();
      }
    }
  }

  #hidePagination() {
    for (const nav of [
      this.refs.primaryPagination,
      this.refs.otherPagination,
    ]) {
      if (nav instanceof HTMLElement) nav.hidden = true;
    }
  }

  /**
   * @param {'primary' | 'other'} stream
   * @param {number} page
   * @param {number} token
   * @param {AbortSignal} signal
   */
  async #replaceStream(stream, page, token, signal) {
    if (!this.isCurrentLoad(token)) return;
    const list =
      stream === "other" ? this.refs.otherList : this.refs.primaryList;
    this.setHostListPending(list, true);
    const nav =
      stream === "other"
        ? this.refs.otherPagination
        : this.refs.primaryPagination;
    if (nav instanceof HTMLElement) nav.hidden = true;
    this.setHostStatus(this.dataset.i18nLoading || "", {
      screenReaderOnly: true,
    });

    try {
      const payload = await this.#fetchPage(stream, page, signal);
      if (!this.isCurrentLoad(token)) return;
      const reviews =
        stream === "other"
          ? (payload.other_language_reviews ?? [])
          : (payload.primary_language_reviews ?? []);
      const pagination =
        stream === "other"
          ? payload.other_language_pagination
          : payload.primary_language_pagination;
      const translations = payload.review_translations ?? {};
      const apiTotal = Number(pagination?.total_pages);
      const keepMetafieldTotal =
        stream === "primary" &&
        this.#filterRating === 0 &&
        page > JUDGE_ME_OFFSET_PAGES;

      if (stream === "other") {
        this.#otherPage = page;
        if (Number.isFinite(apiTotal)) {
          this.#otherTotalPages = apiTotal;
        }
      } else {
        this.#primaryPage = page;
        // `total_pages: 0` is an empty filter, not missing. Deep unfiltered
        // shard totals (14 for 1★) must not replace metafield 521.
        if (
          Number.isFinite(apiTotal) &&
          (apiTotal === 0 || !keepMetafieldTotal)
        ) {
          this.#primaryTotalPages = apiTotal;
        }
      }

      this.paintList(list, reviews, translations, { replace: true });
      this.#renderPagination(stream);
      this.setHostStatus("");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!this.isCurrentLoad(token)) return;
      this.logLoadError("Failed to load reviews", error);
      this.setHostStatus(this.dataset.i18nLoadError || "");
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<import('@theme/mysa-reviews-api').JudgeMeRatingShard[]>}
   */
  async #ensureRatingShards(signal) {
    if (this.#ratingShards) return this.#ratingShards;
    const ratings = [5, 4, 3, 2, 1];
    const shards = await Promise.all(
      ratings.map(async (rating) => {
        try {
          const payload = await fetchWidgetPage({
            signal,
            shopDomain: this.dataset.shopDomain || "",
            productId: this.dataset.productId || "",
            productUrl: absoluteProductUrl(this.dataset.productUrl || ""),
            stream: "primary",
            page: 1,
            perPage: this.dataset.perPage || "10",
            locale: this.dataset.locale || "en",
            primaryLanguage: this.dataset.primaryLanguage || "en",
            filterRating: rating,
            sortBy: DEFAULT_SORT,
            sendSort: true,
          });
          return {
            rating,
            pages:
              Number(payload.primary_language_pagination?.total_pages) || 0,
          };
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") {
            throw error;
          }
          this.logLoadError("Failed to load rating page counts", error);
          return { rating, pages: 0 };
        }
      }),
    );
    this.#ratingShards = shards.filter((shard) => shard.pages > 0);
    return this.#ratingShards;
  }

  /**
   * @param {'primary' | 'other'} stream
   * @param {number} page
   * @param {AbortSignal} signal
   */
  async #fetchPage(stream, page, signal) {
    const usesRatingOrder =
      this.#sortBy === DEFAULT_SORT || this.#sortBy === "highest-rating";
    const shards =
      stream === "primary" &&
      this.#filterRating === 0 &&
      usesRatingOrder &&
      page > JUDGE_ME_OFFSET_PAGES
        ? await this.#ensureRatingShards(signal)
        : [];
    const plan = planWidgetPage({
      requested: page,
      totalPages:
        stream === "other" ? this.#otherTotalPages : this.#primaryTotalPages,
      sortBy: this.#sortBy,
      filterRating: this.#filterRating,
      shards,
    });
    return fetchWidgetPage({
      signal,
      shopDomain: this.dataset.shopDomain || "",
      productId: this.dataset.productId || "",
      productUrl: absoluteProductUrl(this.dataset.productUrl || ""),
      stream,
      page: plan.page,
      perPage: this.dataset.perPage || "10",
      otherPerPage: this.dataset.otherPerPage || "3",
      locale: this.dataset.locale || "en",
      primaryLanguage: this.dataset.primaryLanguage || "en",
      filterRating: plan.filterRating,
      sortBy: plan.sortBy,
      sendSort: this.#sendSortParams || plan.sendSort,
    });
  }

  #syncFilterUi() {
    const histogram = this.refs.histogram;
    if (histogram) {
      for (const row of histogram.querySelectorAll(
        ".mysa-reviews-histogram__row",
      )) {
        if (!(row instanceof HTMLButtonElement)) continue;
        const pressed = Number(row.dataset.rating) === this.#filterRating;
        row.setAttribute("aria-pressed", pressed ? "true" : "false");
      }
    }

    if (this.refs.seeAll) {
      this.refs.seeAll.hidden = this.#filterRating === 0;
    }

    const ratingFilter = this.refs.ratingFilter;
    if (ratingFilter instanceof HTMLSelectElement) {
      const next = String(this.#filterRating || 0);
      if (ratingFilter.value !== next) ratingFilter.value = next;
    }

    this.#renderPagination("primary");
    this.#renderPagination("other");
  }

  /**
   * @param {'primary' | 'other'} stream
   */
  #renderPagination(stream) {
    const nav =
      stream === "other"
        ? this.refs.otherPagination
        : this.refs.primaryPagination;
    if (!(nav instanceof HTMLElement)) return;
    if (this.hasAttribute("aria-busy")) {
      nav.hidden = true;
      return;
    }

    const total =
      stream === "other" ? this.#otherTotalPages : this.#primaryTotalPages;
    const current = stream === "other" ? this.#otherPage : this.#primaryPage;
    nav.hidden = total <= 1;
    nav.replaceChildren();
    if (total <= 1) return;

    nav.append(
      this.#createPageButton({
        stream,
        label: "<",
        ariaLabel: this.dataset.i18nPrevious || "",
        page: current > 1 ? current - 1 : undefined,
        disabled: current <= 1,
      }),
    );

    for (const item of pageItems(current, total)) {
      if (item === "ellipsis") {
        const ellipsis = document.createElement("span");
        ellipsis.className = "mysa-reviews-widget__ellipsis";
        ellipsis.setAttribute("aria-hidden", "true");
        ellipsis.textContent = "…";
        nav.append(ellipsis);
        continue;
      }

      nav.append(
        this.#createPageButton({
          stream,
          page: item,
          current,
          ariaLabel: (this.dataset.i18nPage || "").replaceAll(
            "__PAGE__",
            String(item),
          ),
        }),
      );
    }

    nav.append(
      this.#createPageButton({
        stream,
        label: ">",
        ariaLabel: this.dataset.i18nNext || "",
        page: current < total ? current + 1 : undefined,
        disabled: current >= total,
      }),
    );
  }

  /**
   * @param {{
   *   stream?: 'primary' | 'other',
   *   page?: number,
   *   current?: number,
   *   label?: string,
   *   ariaLabel?: string,
   *   disabled?: boolean,
   * }} options
   */
  #createPageButton(options) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "mysa-reviews-widget__page";
    button.setAttribute("on:click", "mysa-reviews-widget/goToPage");
    button.dataset.stream = options.stream === "other" ? "other" : "primary";
    if (options.page) button.dataset.page = String(options.page);
    if (options.ariaLabel) button.setAttribute("aria-label", options.ariaLabel);
    if (options.page && options.page === options.current) {
      button.setAttribute("aria-current", "page");
    }
    button.disabled = Boolean(options.disabled || !options.page);

    if (options.label) {
      const mark = document.createElement("span");
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = options.label;
      button.append(mark);
    } else {
      button.textContent = String(options.page);
    }

    return button;
  }
}

if (!customElements.get("mysa-reviews-widget")) {
  customElements.define("mysa-reviews-widget", MysaReviewsWidget);
}
